import { NisoloSubmission } from '../hooks/useNisoloSubmissions'
import { NisoloOrderData } from '../hooks/useNisoloOrders'

export type LineOutcome = {
  name: string
  predict: number
  receivedUnits: number
  soldUnits: number
  actualSales: number
  predictedForSold: number
  variance: number
  actualAur: number
}

export type SubmissionOutcome = {
  matched: boolean
  matchedLines: number
  soldUnits: number
  actualSales: number
  predictedForSold: number
  variance: number
  variancePct: number
  predictedAur: number
  actualAur: number
  aurVariancePct: number
  sellThrough: number
  unsoldUnits: number
  remainingPredicted: number
  overPerformers: LineOutcome[]
  underPerformers: LineOutcome[]
}

type BarcodeGroup = { name: string; receivedUnits: number; predictedValue: number }

export function reconcileSubmission(submission: NisoloSubmission, orders: NisoloOrderData | null): SubmissionOutcome {
  // Several receiving lines can share one barcode. Collapse them first so a
  // barcode's sales are only ever claimed once.
  const groups = new Map<string, BarcodeGroup>()
  submission.lines.forEach((line) => {
    if (!line.upc) return
    const existing = groups.get(line.upc) ?? { name: line.name, receivedUnits: 0, predictedValue: 0 }
    existing.receivedUnits += line.units
    existing.predictedValue += line.predict * line.units
    groups.set(line.upc, existing)
  })

  const outcomes: LineOutcome[] = []
  let soldUnits = 0
  let actualSales = 0
  let predictedForSold = 0

  groups.forEach((group, upc) => {
    const actual = orders?.byBarcode[upc]
    if (!actual || actual.units <= 0 || group.receivedUnits <= 0) return

    // A barcode can never sell more units than this submission delivered.
    const cappedUnits = Math.min(actual.units, group.receivedUnits)
    const ratio = cappedUnits / actual.units
    const sales = actual.sales * ratio
    const predictPerUnit = group.predictedValue / group.receivedUnits
    const predicted = predictPerUnit * cappedUnits

    soldUnits += cappedUnits
    actualSales += sales
    predictedForSold += predicted

    outcomes.push({
      name: group.name,
      predict: predictPerUnit,
      receivedUnits: group.receivedUnits,
      soldUnits: cappedUnits,
      actualSales: sales,
      predictedForSold: predicted,
      variance: sales - predicted,
      actualAur: sales / cappedUnits,
    })
  })

  const predictedAur = submission.received > 0 ? submission.estimatedRecovery / submission.received : 0
  const actualAur = soldUnits > 0 ? actualSales / soldUnits : 0
  const unsoldUnits = Math.max(0, submission.received - soldUnits)
  const ranked = outcomes.slice().sort((a, b) => b.variance - a.variance)

  return {
    matched: soldUnits > 0,
    matchedLines: outcomes.length,
    soldUnits,
    actualSales,
    predictedForSold,
    variance: actualSales - predictedForSold,
    variancePct: predictedForSold > 0 ? ((actualSales - predictedForSold) / predictedForSold) * 100 : 0,
    predictedAur,
    actualAur,
    aurVariancePct: predictedAur > 0 ? ((actualAur - predictedAur) / predictedAur) * 100 : 0,
    sellThrough: submission.received > 0 ? (soldUnits / submission.received) * 100 : 0,
    unsoldUnits,
    remainingPredicted: unsoldUnits * predictedAur,
    overPerformers: ranked.filter((item) => item.variance > 0).slice(0, 4),
    underPerformers: ranked.filter((item) => item.variance < 0).slice(-4).reverse(),
  }
}
